import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-soft hover:brightness-110 active:scale-[0.98]",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border-2 border-primary text-primary bg-transparent hover:bg-primary hover:text-primary-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent/10 hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // SASDesk-style premium variants
        hero: "bg-gradient-to-r from-primary to-accent text-white shadow-glow hover:shadow-[0_0_50px_-5px_hsl(var(--primary)/0.5)] hover:brightness-110 active:scale-[0.98]",
        "hero-outline":
          "border border-border/50 text-foreground bg-transparent backdrop-blur-sm hover:bg-muted/50 hover:border-primary/30",
        glow: "bg-primary text-primary-foreground shadow-glow animate-glow-pulse hover:brightness-110 active:scale-[0.98]",
        glass:
          "backdrop-blur-xl bg-white/10 border border-white/20 text-foreground hover:bg-white/20 hover:border-white/30",
        // CTA Verde - Alto contraste para conversión
        cta: "bg-[hsl(142,71%,35%)] text-white hover:bg-[hsl(142,71%,30%)] shadow-lg hover:shadow-xl hover:shadow-[hsl(142,71%,35%)]/20 active:scale-[0.98]",
      },
      size: {
        default: "h-11 px-5 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-13 rounded-xl px-8 text-base",
        xl: "h-14 rounded-xl px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
